/**
 * DOMParser 解析完整文档时，位于内容开头的 <style>/<link> 会按 HTML5 规则被提到 <head>，
 * 而后续只取 body.innerHTML，导致这些标签丢失。此处在序列化前把它们移回 body 开头以保留。
 */
function hoistHeadNodesToBody(doc) {
    if (!doc?.head || !doc?.body) return;
    const nodes = doc.head.querySelectorAll('style, link');
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
        doc.body.insertBefore(nodes[i], doc.body.firstChild);
    }
}

/** table 加线（与 SheetDetail 一致） */
export function formatTableHtml(htmlStr = '') {
    if (!htmlStr || typeof htmlStr !== 'string') return htmlStr;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlStr, 'text/html');
    const tables = doc.querySelectorAll('table');
    tables.forEach((table) => {
        if (!table.hasAttribute('border')) {
            table.setAttribute('border', '1');
        }
        const style = table.getAttribute('style') || '';
        if (!/border-collapse\s*:\s*collapse/i.test(style)) {
            table.setAttribute(
                'style',
                `${style}${style && !style.trim().endsWith(';') ? ';' : ''}border-collapse: collapse;`,
            );
        }
    });
    hoistHeadNodesToBody(doc);
    return doc.body.innerHTML;
}

// 富文本内容差异对比
const DIFF_BLOCK_SELECTOR = 'p,h1,h2,h3,h4,h5,h6,li,blockquote,pre,td,th';
const DIFF_SENTENCE_END_REG = /[.!?;。！？；]/;
const DIFF_INLINE_FORMAT_TAGS = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'DEL', 'STRIKE', 'MARK', 'CODE', 'SUB', 'SUP']);
// const DIFF_FILTER_SELECTOR = 'style,script,img'; // 过滤 style,script,img 标签

function createHtmlDoc(htmlStr = '') {
    const parser = new DOMParser();
    return parser.parseFromString(htmlStr || '', 'text/html');
}

function removeCompareNoise(root) {
    if (!root?.querySelectorAll) return;
    // root.querySelectorAll(DIFF_FILTER_SELECTOR).forEach((node) => node.remove());
    root.querySelectorAll('.match_purple').forEach((node) => {
        node.replaceWith(...node.childNodes);
    });
}

function normalizeCompareText(text = '') {
    return text
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

function getComparableTextSegments(node) {
    const segments = getTextSegmentsWithRanges(node?.textContent || '');
    if (!node) return segments.map((item) => item.compareText).filter(Boolean);

    return segments
        .map((item) => `${item.compareText}|${getTextRangeFormatSignature(node, item.start, item.end)}`)
        .filter(Boolean);
}

function getComparableBlockSignature(node) {
    if (!node) return '';
    const text = normalizeCompareText(node.textContent || '');
    if (!text) return '';

    return `${text}|${getTextRangeFormatSignature(node, 0, node.textContent?.length || 0)}`;
}

function getTextSegmentsWithRanges(text = '') {
    const segments = [];
    let start = 0;

    const pushSegment = (end) => {
        let trimStart = start;
        let trimEnd = end;

        while (trimStart < trimEnd && /\s/.test(text[trimStart])) trimStart += 1;
        while (trimEnd > trimStart && /\s/.test(text[trimEnd - 1])) trimEnd -= 1;

        const compareText = normalizeCompareText(text.slice(trimStart, trimEnd));
        if (compareText) {
            segments.push({
                start: trimStart,
                end: trimEnd,
                compareText,
            });
        }

        start = end;
    };

    for (let i = 0; i < text.length; i += 1) {
        if (DIFF_SENTENCE_END_REG.test(text[i])) {
            pushSegment(i + 1);
        }
    }

    if (start < text.length) {
        pushSegment(text.length);
    }

    return segments;
}

function normalizeStyleText(styleText = '') {
    return styleText
        .split(';')
        .map((item) => item.trim().toLowerCase())
        .filter((item) => /^(font-weight|font-style|text-decoration|font-size|color|background-color)\s*:/.test(item))
        .sort()
        .join(';');
}

function getElementFormatSignature(element) {
    const signature = [];
    const tagName = element.tagName;
    const styleText = normalizeStyleText(element.getAttribute('style') || '');

    if (DIFF_INLINE_FORMAT_TAGS.has(tagName)) {
        signature.push(tagName.toLowerCase());
    }

    if (styleText) {
        signature.push(`style(${styleText})`);
    }

    return signature;
}

function getTextNodeFormatSignature(textNode, root) {
    const signature = [];
    let current = textNode.parentElement;

    while (current && current !== root) {
        signature.unshift(...getElementFormatSignature(current));
        current = current.parentElement;
    }

    return signature.join('>');
}

function getTextRangeFormatSignature(root, startOffset, endOffset) {
    const doc = root.ownerDocument;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const chunks = [];
    let currentOffset = 0;
    let textNode = walker.nextNode();

    while (textNode) {
        const nextOffset = currentOffset + textNode.nodeValue.length;
        const overlapStart = Math.max(startOffset, currentOffset);
        const overlapEnd = Math.min(endOffset, nextOffset);

        if (overlapStart < overlapEnd) {
            const text = normalizeCompareText(textNode.nodeValue.slice(overlapStart - currentOffset, overlapEnd - currentOffset));
            if (text) {
                chunks.push(`${text}:${getTextNodeFormatSignature(textNode, root)}`);
            }
        }

        currentOffset = nextOffset;
        if (currentOffset >= endOffset) break;
        textNode = walker.nextNode();
    }

    return chunks.join('|');
}

function collectComparableBlocks(doc) {
    if (!doc?.body) return [];
    return Array.from(doc.body.querySelectorAll(DIFF_BLOCK_SELECTOR)).filter((node) => {
        return !node.parentElement?.closest(DIFF_BLOCK_SELECTOR);
    });
}

function getComparableBlockItems(htmlStr = '') {
    const doc = createHtmlDoc(htmlStr);
    removeCompareNoise(doc.body);

    return collectComparableBlocks(doc)
        .map((node) => {
            const compareText = getComparableBlockSignature(node);
            return {
                node,
                tagName: node.tagName,
                compareText,
                segments: getComparableTextSegments(node),
            };
        })
        .filter((item) => Boolean(item.compareText));
}

function getLcsMatchedIndices(sourceArr = [], targetArr = []) {
    const sourceLen = sourceArr.length;
    const targetLen = targetArr.length;
    const dp = Array.from({ length: sourceLen + 1 }, () => Array(targetLen + 1).fill(0));

    for (let i = sourceLen - 1; i >= 0; i -= 1) {
        for (let j = targetLen - 1; j >= 0; j -= 1) {
            if (sourceArr[i] === targetArr[j]) {
                dp[i][j] = dp[i + 1][j + 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
            }
        }
    }

    const matchedTargetIndices = new Set();
    let i = 0;
    let j = 0;

    while (i < sourceLen && j < targetLen) {
        if (sourceArr[i] === targetArr[j]) {
            matchedTargetIndices.add(j);
            i += 1;
            j += 1;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            i += 1;
        } else {
            j += 1;
        }
    }

    return matchedTargetIndices;
}

function findBestSourceBlock(targetItem, sourceItems = []) {
    let bestItem = null;
    let bestScore = -Infinity;

    sourceItems.forEach((sourceItem) => {
        const matchedCount = getLcsMatchedIndices(sourceItem.segments, targetItem.segments).size;
        const sameTagScore = sourceItem.tagName === targetItem.tagName ? 0.5 : 0;
        const lengthPenalty = Math.abs(sourceItem.segments.length - targetItem.segments.length) * 0.1;
        const score = matchedCount * 2 + sameTagScore - lengthPenalty;

        if (score > bestScore) {
            bestScore = score;
            bestItem = sourceItem;
        }
    });

    return bestItem;
}

function createRangeFromTextOffsets(root, startOffset, endOffset) {
    const doc = root.ownerDocument;
    const range = doc.createRange();
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let currentOffset = 0;
    let startSet = false;
    let endSet = false;
    let textNode = walker.nextNode();

    while (textNode) {
        const nextOffset = currentOffset + textNode.nodeValue.length;

        if (!startSet && startOffset >= currentOffset && startOffset <= nextOffset) {
            range.setStart(textNode, startOffset - currentOffset);
            startSet = true;
        }

        if (!endSet && endOffset >= currentOffset && endOffset <= nextOffset) {
            range.setEnd(textNode, endOffset - currentOffset);
            endSet = true;
            break;
        }

        currentOffset = nextOffset;
        textNode = walker.nextNode();
    }

    return startSet && endSet ? range : null;
}

function wrapChangedTextSegments(node, changedIndices = []) {
    const segments = getTextSegmentsWithRanges(node.textContent || '');
    const doc = node.ownerDocument;

    changedIndices
        .slice()
        .sort((a, b) => b - a)
        .forEach((index) => {
            const segment = segments[index];
            if (!segment || segment.start >= segment.end) return;

            const range = createRangeFromTextOffsets(node, segment.start, segment.end);
            if (!range || range.collapsed) return;

            const wrapper = doc.createElement('span');
            wrapper.className = 'match_purple';
            wrapper.appendChild(range.extractContents());
            range.insertNode(wrapper);
        });
}

export function diffEditorContent(source, target) {
    if (!target || typeof target !== 'string') return target;
    if (!source || typeof source !== 'string') return target;

    const sourceBlockItems = getComparableBlockItems(source);
    const sourceBlockTexts = sourceBlockItems.map((item) => item.compareText);
    const targetDoc = createHtmlDoc(target);
    hoistHeadNodesToBody(targetDoc);
    removeCompareNoise(targetDoc.body);

    const targetBlocks = collectComparableBlocks(targetDoc);
    const targetComparableBlocks = targetBlocks
        .map((node) => ({
            node,
            tagName: node.tagName,
            compareText: getComparableBlockSignature(node),
            segments: getComparableTextSegments(node),
        }))
        .filter((item) => Boolean(item.compareText));
    const targetBlockTexts = targetComparableBlocks.map((item) => item.compareText);

    if (!targetBlocks.length || !targetBlockTexts.length) {
        return formatTableHtml(target);
    }

    const matchedTargetIndices = getLcsMatchedIndices(sourceBlockTexts, targetBlockTexts);

    targetComparableBlocks.forEach((targetItem, index) => {
        if (matchedTargetIndices.has(index)) return;

        const sourceItem = findBestSourceBlock(targetItem, sourceBlockItems);
        const matchedSegmentIndices = getLcsMatchedIndices(sourceItem?.segments || [], targetItem.segments);
        const changedSegmentIndices = targetItem.segments
            .map((_, segmentIndex) => segmentIndex)
            .filter((segmentIndex) => !matchedSegmentIndices.has(segmentIndex));

        if (changedSegmentIndices.length) {
            wrapChangedTextSegments(targetItem.node, changedSegmentIndices);
        }
    });

    return formatTableHtml(targetDoc.body.innerHTML);
}
